import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MultiSelectPanelDefinition } from "@/types/actions";
import { useQuestionTimer } from "@/hooks/useQuestionTimer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

interface MultiSelectPanelProps {
  panel: MultiSelectPanelDefinition;
  /** Resets the countdown between rounds — panel IDs are reused by game modes. */
  requestId: string;
  onSubmit: (selectedIds: string[]) => void;
  disabled?: boolean;
}

export const MultiSelectPanel: React.FC<MultiSelectPanelProps> = ({
  panel,
  requestId,
  onSubmit,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { timeLeft: timeRemaining } = useQuestionTimer(
    panel.timeout_seconds,
    requestId
  );

  const toggleOption = (optionId: string) => {
    if (disabled) return;

    setSelected((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(optionId)) {
        newSelected.delete(optionId);
      } else {
        // Check max_selections constraint
        if (
          panel.max_selections !== undefined &&
          panel.max_selections !== null &&
          newSelected.size >= panel.max_selections
        ) {
          return prev; // Don't add if at max
        }
        newSelected.add(optionId);
      }
      return newSelected;
    });
  };

  const handleSubmit = () => {
    if (disabled || !isValid) return;
    onSubmit(Array.from(selected));
  };

  // Validation
  const isValid =
    selected.size >= panel.min_selections &&
    (panel.max_selections === undefined ||
      panel.max_selections === null ||
      selected.size <= panel.max_selections);

  const getLayoutClass = () => {
    switch (panel.layout) {
      case "horizontal":
        return "flex flex-row flex-wrap gap-2";
      case "grid":
        return "grid grid-cols-2 gap-2";
      case "vertical":
      default:
        return "flex flex-col gap-2";
    }
  };

  return (
    <Card className="overflow-hidden flex flex-col min-h-0">
      {/* The card itself must not scroll — its border and rounded corners have to
          stay in view; everything inside it scrolls as one area instead. */}
      <div className="p-4 flex flex-col gap-4 min-h-0 overflow-y-auto overflow-x-hidden">
        {/* Header */}
        {(panel.title || panel.description) && (
          <div className="flex flex-col gap-1">
            {panel.title && (
              <h2 className="text-lg font-semibold text-card-foreground">
                {panel.title}
              </h2>
            )}
            {panel.description && (
              <p className="text-xs text-muted-foreground">
                {panel.description}
              </p>
            )}
          </div>
        )}

        {/* Selection Counter */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            {t('game.selectedLabel')} {selected.size}
            {panel.max_selections !== undefined && panel.max_selections !== null
              ? ` / ${panel.max_selections}`
              : ""}
          </span>
          {timeRemaining !== null && timeRemaining > 0 && (
            <span className="text-secondary">
              {Math.floor(timeRemaining / 60)}:
              {String(timeRemaining % 60).padStart(2, "0")}
            </span>
          )}
        </div>

        {/* Options */}
        <div className={getLayoutClass()}>
          {panel.options.map((option) => {
            const isSelected = selected.has(option.id);
            const isDisabled = disabled || option.disabled;

            return (
              <button
                key={option.id}
                onClick={() => toggleOption(option.id)}
                disabled={isDisabled}
                className={`
                  relative flex items-center justify-between gap-2 px-4 py-3
                  transition-colors duration-300 font-bold text-left text-sm
                  overflow-hidden rounded-md
                  ${
                    isSelected
                      ? "bg-primary/10 border-2 border-primary text-card-foreground shadow-sm"
                      : "bg-card border-2 border-border text-card-foreground hover:bg-accent"
                  }
                  ${isDisabled ? "opacity-30 cursor-not-allowed" : "cursor-pointer active:scale-95 active:brightness-125"}
                `}
              >
                <div className="flex flex-col gap-1 flex-1 relative z-10">
                  <span className="text-xs">{option.label}</span>
                  {option.description && (
                    <span className="text-[10px] opacity-75 normal-case tracking-normal">
                      {option.description}
                    </span>
                  )}
                </div>

                {/* Checkbox indicator */}
                <div className="flex-shrink-0 relative z-10 pointer-events-none">
                  <Checkbox checked={isSelected} tabIndex={-1} />
                </div>
              </button>
            );
          })}
        </div>

        {/* Validation Message */}
        {!isValid && selected.size > 0 && (
          <p className="text-xs text-destructive animate-pulse">
            {selected.size < panel.min_selections
              ? t("game.selectAtLeast", { count: panel.min_selections })
              : t("game.selectAtMost", { count: panel.max_selections })}
          </p>
        )}

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          variant="default"
          size="lg"
          className="w-full min-h-touch"
          disabled={disabled || !isValid}
        >
          {panel.submit_label}
        </Button>
      </div>
    </Card>
  );
};
